// Flying archetype (MOB-2 flying: ghast). PURE (ENG-1).
//
// Free 3D drift that keeps a preferred hover height above the ground; when a
// non-invulnerable player is within range with a clear line of sight, it turns to
// face them and lobs a fireball on the mob's `fireInterval` cadence (M7.2).
//
// `hunts` (M53.3, ARCH §29.11, CNT-3 barn_owl) swaps the player-attack branch for
// a hunt of another mob species entirely — gated on the field so ghast/phantom/bee
// are untouched (§29.11's own "existing flyers keep unchanged trajectories" Verify).
import { FLYING, OWL } from '../../config';
import type { MobDef } from '../../registry/schemas';
import type { Entity, MobContext, MobWorld } from '../entity';
import { difficultyScale } from './shared';
import { fireFireballAt } from '../projectiles/fireball';

/** Contact distance for a hunting flyer's kill (§29.11 "reaches and kills it"). */
const HUNT_CONTACT = 0.8;

/** Ground Y under (x,z): scan down for the first solid block. */
function groundBelow(world: MobWorld, x: number, y: number, z: number): number {
  const ix = Math.floor(x), iz = Math.floor(z);
  for (let yy = Math.floor(y); yy > Math.floor(y) - 64; yy--) {
    if (world.isSolid(ix, yy, iz)) return yy + 1;
  }
  return Math.floor(y) - 64;
}

/** Coarse line-of-sight: sample the segment at ~1-block steps for a solid block. */
function hasLineOfSight(world: MobWorld, x0: number, y0: number, z0: number, x1: number, y1: number, z1: number): boolean {
  const dx = x1 - x0, dy = y1 - y0, dz = z1 - z0;
  const steps = Math.max(1, Math.ceil(Math.hypot(dx, dy, dz)));
  for (let i = 1; i < steps; i++) {
    const t = i / steps;
    if (world.isSolid(Math.floor(x0 + dx * t), Math.floor(y0 + dy * t), Math.floor(z0 + dz * t))) return false;
  }
  return true;
}

export function flying(e: Entity, def: MobDef, ctx: MobContext): void {
  if (def.hunts) { huntingFlyer(e, def, ctx); return; }
  const { world, frame, sys } = ctx;
  const target = sys.nearestPlayer(frame, e.x, e.y, e.z, FLYING.ATTACK_RANGE);
  const scale = difficultyScale(frame.difficulty);

  // Re-pick a wander target periodically (3D), biased to the hover altitude.
  if (--e.wanderTimer <= 0) {
    e.wanderTimer = FLYING.WANDER_INTERVAL + ctx.rng.nextInt(FLYING.WANDER_INTERVAL);
    e.wanderAngle = ctx.rng.nextFloat() * Math.PI * 2;
    e.data.wishAlt = ctx.rng.nextFloat() * 6 - 3; // small vertical drift
  }
  const desiredY = groundBelow(world, e.x, e.y, e.z) + FLYING.HOVER_HEIGHT + (e.data.wishAlt ?? 0);
  let vx = Math.cos(e.wanderAngle) * FLYING.MAX_SPEED;
  let vz = Math.sin(e.wanderAngle) * FLYING.MAX_SPEED;
  const vy = Math.sign(desiredY - e.y) * FLYING.MAX_SPEED * 0.5;

  if (target && scale > 0) {
    const pl = frame.players[target.idx]!;
    e.targetIdx = target.idx;
    // Hold position and face the player while shooting.
    const dx = pl.x - e.x, dz = pl.z - e.z;
    const d = Math.hypot(dx, dz) || 1;
    vx = (dx / d) * FLYING.MAX_SPEED * 0.3; // drift slowly toward
    vz = (dz / d) * FLYING.MAX_SPEED * 0.3;
    e.yaw = Math.atan2(-dx, -dz);

    e.data.fireCd = (e.data.fireCd ?? 0) - 1;
    const eye = pl.y + pl.eyeY;
    if (e.data.fireCd <= 0 && hasLineOfSight(world, e.x, e.y + 1.5, e.z, pl.x, eye, pl.z)) {
      fireFireballAt(sys, e, pl.x, eye, pl.z);
      e.data.fireCd = def.fireInterval;
    }
  } else {
    e.targetIdx = -1;
  }

  e.wishX = vx; e.wishY = vy; e.wishZ = vz;
}

/** Low hover altitude for a hunting flyer while wandering with no target — an
 *  owl coasts near roof/tree height, not the ghast's sky-high `FLYING.HOVER_HEIGHT`
 *  (§29.11: existing flyers' own hover is untouched by this being a shared const). */
const HUNT_HOVER_HEIGHT = 6;

/** `hunts` behaviour (M53.3, CNT-3): below `OWL.HUNT_LIGHT_MAX` block light, target
 *  the nearest live match of `def.hunts` within `OWL.HUNT_RADIUS`, fly to it, kill it
 *  on contact, then rest `OWL.REST_TICKS` before picking a new target. In daylight,
 *  or while resting, it only wanders — and NEVER targets or damages a player. */
function huntingFlyer(e: Entity, def: MobDef, ctx: MobContext): void {
  const { world, sys } = ctx;

  if ((e.data.restTicks ?? 0) > 0) {
    e.data.restTicks = (e.data.restTicks as number) - 1;
    driftWander(e, ctx);
    return;
  }

  const dark = world.getBlockLight(Math.floor(e.x), Math.floor(e.y), Math.floor(e.z)) < OWL.HUNT_LIGHT_MAX;
  const preyType = def.hunts!;
  const prey = dark ? sys.nearestEntity(e, OWL.HUNT_RADIUS, (o) => o.type === preyType) : null;

  if (!prey) { e.targetIdx = -1; driftWander(e, ctx); return; }

  const dx = prey.x - e.x, dy = prey.y + 0.2 - e.y, dz = prey.z - e.z;
  const dist = Math.hypot(dx, dy, dz);
  if (dist <= HUNT_CONTACT) {
    sys.damage(prey, prey.health + 1, ctx.frame, 0, 0, false);
    e.data.restTicks = OWL.REST_TICKS;
    e.wishX = 0; e.wishY = 0; e.wishZ = 0;
    return;
  }
  const d = dist || 1;
  e.wishX = (dx / d) * FLYING.MAX_SPEED;
  e.wishY = (dy / d) * FLYING.MAX_SPEED;
  e.wishZ = (dz / d) * FLYING.MAX_SPEED;
  e.yaw = Math.atan2(-dx, -dz);
}

/** Gentle 3D wander at `HUNT_HOVER_HEIGHT`, reusing `FLYING`'s wander-timer shape
 *  (own angle/altitude state so it never collides with a non-hunting flyer's). */
function driftWander(e: Entity, ctx: MobContext): void {
  const { world } = ctx;
  if (--e.wanderTimer <= 0) {
    e.wanderTimer = FLYING.WANDER_INTERVAL + ctx.rng.nextInt(FLYING.WANDER_INTERVAL);
    e.wanderAngle = ctx.rng.nextFloat() * Math.PI * 2;
  }
  const desiredY = groundBelow(world, e.x, e.y, e.z) + HUNT_HOVER_HEIGHT;
  e.wishX = Math.cos(e.wanderAngle) * FLYING.MAX_SPEED;
  e.wishZ = Math.sin(e.wanderAngle) * FLYING.MAX_SPEED;
  e.wishY = Math.sign(desiredY - e.y) * FLYING.MAX_SPEED * 0.5;
}
